/* Welke taal, en waarom.

   Volgorde, van sterk naar zwak:

     1. `?lang=en` / `?lang=ru` — een expliciete keuze, wordt vastgelegd.
     2. Wat er onder `gorodn_locale` staat — een eerdere keuze.
     3. Wie je bent: R leest Engels, Н leest Russisch.
     4. Russisch, zolang nog niemand weet wie er kijkt.

   Stap 3 is een default en géén keuze: hij wordt niet weggeschreven. Zou hij
   dat wel doen, dan kon de schakelaar nooit meer «terug naar standaard», en
   zou een gedeeld toestel de eerste gebruiker vastleggen voor de tweede.

   `gorodn_locale` is bewust geen privédata: het staat niet in
   PRIVATE_LOCAL_KEYS en overleeft dus een logout. In welke taal je leest is
   een eigenschap van jou en dit toestel, niet van een sessie -- iemand die
   opnieuw inlogt en ineens Russisch krijgt, denkt dat de app stuk is. */
import { createContext, useContext } from "react";
import { DICTS, type Dict, type Locale } from "./dict";

export const LOCALE_KEY = "gorodn_locale";
const FALLBACK: Locale = "ru";

/** Standaardtaal per gebruiker, zolang er geen expliciete keuze is. */
export const localeForHandle = (handle?: string | null): Locale =>
  handle === "R" ? "en" : handle === "N" ? "ru" : FALLBACK;

const isLocale = (v: unknown): v is Locale => v === "ru" || v === "en";

export const storedLocale = (): Locale | null => {
  try {
    const v = localStorage.getItem(LOCALE_KEY);
    return isLocale(v) ? v : null;
  } catch { return null; }
};

export function storeLocale(l: Locale): void {
  try { localStorage.setItem(LOCALE_KEY, l); } catch { /* privémodus */ }
}

/** De taal uit de URL, als die er staat. Wordt meteen vastgelegd: wie
 *  `?lang=en` opent, verwacht dat de app Engels blíjft. */
export function localeFromQuery(): Locale | null {
  const v = new URLSearchParams(location.search).get("lang");
  if (!isLocale(v)) return null;
  storeLocale(v);
  return v;
}

/** De taal zodra we de gebruiker kennen. Een vastgelegde keuze wint altijd. */
export const localeFor = (handle?: string | null): Locale =>
  storedLocale() ?? localeForHandle(handle);

export interface LocaleValue {
  locale: Locale;
  t: Dict;
  /** Expliciete keuze van de gebruiker: wordt vastgelegd. */
  setLocale: (l: Locale) => void;
}

export const LocaleContext = createContext<LocaleValue>({
  locale: FALLBACK, t: DICTS[FALLBACK], setLocale: () => { /* buiten provider */ },
});

/** De vertalingen. Eén hook, altijd dezelfde vorm -- nooit conditioneel. */
export const useT = (): Dict => useContext(LocaleContext).t;
export const useLocale = (): LocaleValue => useContext(LocaleContext);

/** De vraag van de dag in de taal van deze lezer.
 *
 *  Gecureerde inhoud, geen UI-tekst: hij komt uit de database. Ontbreekt de
 *  gevraagde taal -- het Engelse v9-dek heeft nog geen Russische tegenhangers
 *  -- dan tonen we het origineel. Automatisch vertalen doen we niet: dit is
 *  privé-inhoud tussen twee mensen en die gaat niet naar een externe dienst. */
export const promptText = (
  prompt: { original: string; textRu?: string | null; textEn?: string | null }
    | null | undefined,
  locale: Locale,
): string | null => {
  if (!prompt) return null;
  const wanted = locale === "en" ? prompt.textEn : prompt.textRu;
  return wanted?.trim() || prompt.original;
};

/** Taalcode voor `Intl` en voor het `lang`-attribuut op <html>. */
export const bcp47 = (l: Locale): string => (l === "en" ? "en" : "ru");
